<script setup>
import { computed } from "vue";
import { Position, Severity, TuBadge, TuButton, TuFeedbackBanner, TuTooltipWrapper } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import { isNil } from "lodash-es";

import useTranslation from "@Composables/useTranslation";

const emits = defineEmits(["body-click", "banner-click"]);
const props = defineProps({
  planName: {
    type: String,
    required: true,
  },
  planFeatures: {
    type: Array,
    required: true,
  },
  bannerProps: {
    type: Object,
    default: null,
  },
  planPrice: {
    type: String,
    default: null,
  },
  planInterval: {
    type: String,
    default: "yr",
  },
  renewalDate: {
    type: String,
    default: null,
  },
  isBannerVisible: {
    type: Boolean,
    default: false,
  },
  isLegacy: {
    type: Boolean,
    default: false,
  },
  ctaButtonProps: {
    type: Object,
    default: null,
  },
});

const { __ } = useTranslation();
const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5);

const isMobileView = smallerOrEqual("xl");
const hasPrice = computed(() => !isNil(props.planPrice));
const hasRenewalDate = computed(() => !isNil(props.renewalDate));
const hasCtaButton = computed(() => !isNil(props.ctaButtonProps));
const legacyTooltip = __("subscriptions.legacy_tooltip");
const badgeLabel = __("subscriptions.legacy_badge");
</script>

<template>
  <div
    class="tu-plan-information-card"
    :class="{
      'tu-plan-information-card--mobile': isMobileView,
      'tu-plan-information-card--with-alert-banner': bannerProps?.feedbackSeverity === Severity.ALERT,
      'tu-plan-information-card--with-secondary-banner': bannerProps?.feedbackSeverity === Severity.SECONDARY,
    }"
  >
    <TuFeedbackBanner
      v-if="isBannerVisible"
      class="tu-plan-information-card__banner"
      is-attached
      :feedback-button="bannerProps.feedbackButton"
      :is-closable="false"
      :severity="bannerProps.feedbackSeverity"
      @cta-click="emits('banner-click')"
    >
      <template #content>
        <Component
          :is="bannerProps.feedbackContent"
          v-bind="bannerProps.feedbackContentProps"
        />
      </template>
    </TuFeedbackBanner>

    <div class="tu-plan-information-card__body">
      <TuTooltipWrapper
        v-if="isLegacy && isMobileView"
        class="tu-plan-information-card__badge-legacy"
        :tooltip="legacyTooltip"
      >
        <TuBadge
          :icon-position="Position.END"
          :label="badgeLabel"
          icon-class="fa-solid fa-circle-info"
        />
      </TuTooltipWrapper>
      <div class="tu-plan-information-card__features">
        <div class="tu-plan-information-card__row">
          <h6 class="tu-plan-information-card__title">
            Plan:
          </h6>
          <div class="tu-plan-information-card__content">
            <div class="tu-plan-information-card__name-wrapper">
              <p class="tu-plan-information-card__name _plan_name">
                {{ planName }}
              </p>
              <TuTooltipWrapper
                v-if="isLegacy && !isMobileView"
                class="tu-plan-information-card__badge-legacy"
                :tooltip="legacyTooltip"
              >
                <TuBadge
                  :icon-position="Position.END"
                  :label="badgeLabel"
                  icon-class="fa-solid fa-circle-info"
                />
              </TuTooltipWrapper>
            </div>
            <ul class="tu-plan-information-card__limits">
              <li
                v-for="feature in planFeatures"
                :key="feature.id"
                class="tu-plan-information-card__limits-item _feature"
              >
                <i
                  class="tu-plan-information-card__limits-icon fa-solid"
                  :class="feature.icon"
                />
                <span class="tu-plan-information-card__limits-label">
                  {{ feature.label }}
                </span>
              </li>
            </ul>
          </div>
        </div>
        <div
          v-if="hasPrice"
          class="tu-plan-information-card__row"
        >
          <h6 class="tu-plan-information-card__title">
            Price:
          </h6>
          <div class="tu-plan-information-card__content _price">
            {{ planPrice }} / {{ planInterval }} *
          </div>
        </div>
        <div
          v-if="hasRenewalDate"
          class="tu-plan-information-card__row"
        >
          <h6 class="tu-plan-information-card__title">
            Renews:
          </h6>
          <div class="tu-plan-information-card__content _renews">
            {{ renewalDate }}
          </div>
        </div>
      </div>
      <div class="tu-plan-information-card__cta-wrapper">
        <TuButton
          v-if="hasCtaButton"
          class="tu-plan-information-card__cta"
          tabindex="0"
          :is-block="isMobileView"
          v-bind="ctaButtonProps"
          @click="emits('body-click')"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.tu-plan-information-card {
  $self: &;

  background-color: color.$background-secondary;
  border: border.$primary;
  border-radius: border.$medium-radius;
  overflow: hidden;

  &__banner {
    background-color: color.$alert;
  }

  &__body {
    display: flex;
    padding: spacing.$xxs;
  }

  &__features {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: spacing.$xxs;
  }

  &__row {
    display: flex;
  }

  &__title {
    flex-basis: 200px;
    margin-bottom: 0;
    line-height: 1.5;
  }

  &__limits {
    padding-left: 0;
    margin-bottom: 0;
    font-size: typography.$xs-font-size;
    display: flex;
    flex-direction: column;
    gap: spacing.$xxxs;

    &-item {
      list-style-type: none;
      display: flex;
      align-items: center;
    }

    &-icon {
      display: block;
      margin-right: spacing.$xxxs;

      &::before {
        height: 12px;
        width: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &.fa-check {
        color: color.$secondary;
      }

      &.fa-xmark {
        color: color.$alert;
      }
    }

    &-label {
      font-weight: typography.$font-weight-semi-bold;
    }
  }

  &__cta-wrapper {
    display: flex;
    align-items: flex-end;
  }

  &__name {
    margin: 0;
  }

  &__name-wrapper {
    display: flex;
    gap: spacing.$xxs;
    align-items: center;
    margin-bottom: spacing.$xxs;
  }

  &--mobile {
    #{ $self }__body {
      flex-direction: column;
      gap: spacing.$xxxs;
    }

    #{ $self }__title {
      flex-basis: 90px;
    }

    #{ $self }__plan-content {
      flex-flow: column;
    }

    #{ $self }__badge-legacy {
      align-self: flex-end;
    }

    #{ $self }__plan-content {
      flex-flow: column;
    }

    #{ $self }__badge-legacy {
      align-self: flex-end;
    }
  }

  &--with-alert-banner {
    border-color: color.$alert;
  }

  &--with-secondary-banner {
    border-color: color.$secondary;
  }
}
</style>
